import { useSnapshot } from 'valtio'
import styled from 'styled-components'
import { tState } from '../../hooks/useTranslation'

// ISO weekday numbers: 1=Monday … 7=Sunday.
// January 1, 2024 is a Monday, so new Date(2024, 0, isoDay) maps 1→Mon … 7→Sun.
const ISO_DAYS = [1, 2, 3, 4, 5, 6, 7]

// Normalize Intl short names to a uniform 3-char title-cased label.
// Different locales produce "Mon", "mo.", "lun.", "ma" etc. — strip punctuation,
// take first 3 chars, capitalize first letter and lowercase the rest.
const getDayLabel = (isoDay, locale) => {
    const raw = new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(new Date(2024, 0, isoDay))
    const clean = raw.replace(/[^a-zA-ZÀ-ÿ]/g, '').slice(0, 3)
    return clean.charAt(0).toUpperCase() + clean.slice(1).toLowerCase()
}

// ── Styled ──────────────────────────────────────────────────────────────────

const Wrapper = styled.div`
    display: flex;
    gap: 0.35rem;
    flex-wrap: wrap;
    padding: 0.15rem 0;
`

const DayBtn = styled.button`
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    border: 1.5px solid ${({ $active, theme }) => $active ? theme.btn.primary : theme.area.lightBorder};
    background: ${({ $active, theme }) => $active ? theme.btn.primary : 'transparent'};
    color: ${({ $active, theme }) => $active ? theme.card.background : theme.font.label};
    font-size: var(--font-XXS);
    font-weight: 600;
    cursor: pointer;
    transition: background 0.12s, border-color 0.12s, color 0.12s;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &:hover {
        border-color: ${({ theme }) => theme.btn.primary};
        color: ${({ $active, theme }) => $active ? theme.card.background : theme.btn.primary};
    }
`

// ── Component ────────────────────────────────────────────────────────────────
// value: number[] | null | undefined
//   - null/undefined/empty → all 7 days shown as active (new-record default)
//   - [1,3,5] → only those days active
// setValue: emits { target: { name, value: number[] } }

const WeekDayPicker = ({ name, value, setValue }) => {
    const { currentLang } = useSnapshot(tState)

    // Treat null / undefined / [] as "all days active" for display
    const activeDays = Array.isArray(value) && value.length > 0 ? value : ISO_DAYS

    const toggle = (day) => {
        const next = activeDays.includes(day)
            ? activeDays.filter(d => d !== day)
            : [...activeDays, day].sort((a, b) => a - b)
        setValue({ target: { name, value: next } })
    }

    return (
        <Wrapper>
            {ISO_DAYS.map(day => (
                <DayBtn
                    key={day}
                    type="button"
                    $active={activeDays.includes(day)}
                    onClick={() => toggle(day)}
                    title={new Intl.DateTimeFormat(currentLang, { weekday: 'long' }).format(new Date(2024, 0, day))}
                >
                    {getDayLabel(day, currentLang)}
                </DayBtn>
            ))}
        </Wrapper>
    )
}

export default WeekDayPicker
